'use client'

import ConfirmModal from '@/components/confirm-modal'
import {
    WHATSAPP_BROADCAST_RECIPIENT_STATUS_LABELS,
    WHATSAPP_BROADCAST_STATUS_COLORS,
    WHATSAPP_BROADCAST_STATUS_LABELS,
    WhatsappBroadcast,
    WhatsappBroadcastRecipient,
    WhatsappBroadcastRecipientStatus,
    WhatsappBroadcastStats,
    cancelWhatsappBroadcast,
    deleteWhatsappBroadcast,
    getWhatsappBroadcast,
    getWhatsappBroadcastRecipients,
    getWhatsappBroadcastStats,
    importWhatsappBroadcastRecipients,
    pauseWhatsappBroadcast,
    startWhatsappBroadcast,
} from '@/lib/api/messaging/whatsapp-broadcasts-api'
import { useResourcePermissions } from '@/lib/hooks/use-resource-permissions'
import { useCallback, useEffect, useRef, useState } from 'react'
import { MdArrowBack, MdCancel, MdDelete, MdEdit, MdPause, MdPlayArrow, MdUploadFile } from 'react-icons/md'
import { useNavigate, useParams } from 'react-router-dom'
import { toast } from 'react-toastify'
import { MetaLimitCard } from './meta-limit-card'
import { parseRecipientsCsv } from './parse-recipients-csv'

const IMPORT_CHUNK = 1000
const PAGE_SIZE = 50

type ConfirmAction = 'start' | 'cancel' | 'delete' | null

const formatDateTime = (value: string | null) =>
    value ? new Date(value).toLocaleString('pt-BR', { dateStyle: 'short', timeStyle: 'short' }) : '-'

const StatCard = ({ label, value, hint }: { label: string; value: number; hint?: string }) => (
    <div className="rounded-lg border border-base-300 p-3">
        <div className="text-xs text-base-content/60">{label}</div>
        <div className="text-xl font-semibold tabular-nums">{value.toLocaleString('pt-BR')}</div>
        {hint && <div className="text-xs text-base-content/50">{hint}</div>}
    </div>
)

const WhatsappBroadcastDetailPage = () => {
    const { id } = useParams()
    const broadcastId = Number(id)
    const navigate = useNavigate()
    const { canRead, canUpdate, canDelete } = useResourcePermissions('messaging', 'whatsapp_broadcasts')

    const [broadcast, setBroadcast] = useState<WhatsappBroadcast | null>(null)
    const [stats, setStats] = useState<WhatsappBroadcastStats | null>(null)
    const [recipients, setRecipients] = useState<WhatsappBroadcastRecipient[]>([])
    const [recipientTotal, setRecipientTotal] = useState(0)
    const [statusFilter, setStatusFilter] = useState<WhatsappBroadcastRecipientStatus | ''>('')
    const [page, setPage] = useState(1)
    const [importing, setImporting] = useState<{ done: number; total: number } | null>(null)
    const [confirm, setConfirm] = useState<ConfirmAction>(null)
    const [acting, setActing] = useState(false)
    const fileRef = useRef<HTMLInputElement>(null)

    const load = useCallback(async () => {
        const [b, s] = await Promise.all([getWhatsappBroadcast(broadcastId), getWhatsappBroadcastStats(broadcastId)])
        if (b.success) setBroadcast(b.result)
        else toast.error(b.error || 'Disparo não encontrado')
        if (s.success) setStats(s.result)
    }, [broadcastId])

    const loadRecipients = useCallback(async () => {
        const res = await getWhatsappBroadcastRecipients(broadcastId, {
            status: statusFilter || undefined,
            page,
            limit: PAGE_SIZE,
        })
        if (res.success) {
            setRecipients(res.result.items)
            setRecipientTotal(res.result.total)
        }
    }, [broadcastId, statusFilter, page])

    useEffect(() => {
        if (canRead) load()
    }, [canRead, load])

    useEffect(() => {
        if (canRead) loadRecipients()
    }, [canRead, loadRecipients])

    // Em andamento, os números mudam a cada ciclo do envio (2 min) e a cada entrega.
    useEffect(() => {
        if (broadcast?.status !== 'RUNNING') return
        const timer = setInterval(() => {
            load()
            loadRecipients()
        }, 30_000)
        return () => clearInterval(timer)
    }, [broadcast?.status, load, loadRecipients])

    const handleFile = async (file: File) => {
        const { rows, ignored } = parseRecipientsCsv(await file.text())
        if (rows.length === 0) {
            toast.error('Nenhum telefone válido no arquivo. A primeira coluna (ou a coluna "telefone") deve ter o número.')
            return
        }
        let imported = 0
        let duplicates = 0
        let invalid = ignored
        setImporting({ done: 0, total: rows.length })
        try {
            for (let i = 0; i < rows.length; i += IMPORT_CHUNK) {
                const res = await importWhatsappBroadcastRecipients(broadcastId, rows.slice(i, i + IMPORT_CHUNK))
                if (!res.success) {
                    toast.error(res.error || 'Falha ao importar')
                    break
                }
                imported += res.result.imported
                duplicates += res.result.duplicates
                invalid += res.result.invalid
                setImporting({ done: Math.min(i + IMPORT_CHUNK, rows.length), total: rows.length })
            }
            toast.success(
                `${imported.toLocaleString('pt-BR')} contatos importados` +
                    (duplicates ? `, ${duplicates.toLocaleString('pt-BR')} repetidos` : '') +
                    (invalid ? `, ${invalid.toLocaleString('pt-BR')} sem telefone válido` : ''),
            )
        } finally {
            setImporting(null)
            if (fileRef.current) fileRef.current.value = ''
            load()
            loadRecipients()
        }
    }

    const runAction = async (action: () => Promise<{ success: boolean; error?: string }>, ok: string) => {
        setActing(true)
        try {
            const res = await action()
            if (res.success) {
                toast.success(ok)
                await load()
            } else {
                toast.error(res.error || 'Não foi possível concluir')
            }
        } finally {
            setActing(false)
            setConfirm(null)
        }
    }

    if (!canRead) {
        return (
            <div className="p-6">
                <div className="alert alert-warning">Você não tem permissão para acessar os disparos.</div>
            </div>
        )
    }
    if (!broadcast) {
        return (
            <div className="flex justify-center p-10">
                <span className="loading loading-spinner loading-md" />
            </div>
        )
    }

    const editable = broadcast.status === 'DRAFT' || broadcast.status === 'PAUSED'
    const closed = broadcast.status === 'COMPLETED' || broadcast.status === 'CANCELED'
    const pages = Math.max(1, Math.ceil(recipientTotal / PAGE_SIZE))

    return (
        <div className="p-6 space-y-4">
            <div className="flex items-start justify-between flex-wrap gap-3">
                <div className="flex items-start gap-3">
                    <button
                        type="button"
                        className="btn btn-ghost btn-sm btn-square"
                        onClick={() => navigate('/messaging/whatsapp-broadcasts')}
                    >
                        <MdArrowBack size={20} />
                    </button>
                    <div>
                        <div className="flex items-center gap-2 flex-wrap">
                            <h1 className="text-2xl font-bold">{broadcast.name}</h1>
                            <span className={`badge ${WHATSAPP_BROADCAST_STATUS_COLORS[broadcast.status]}`}>
                                {WHATSAPP_BROADCAST_STATUS_LABELS[broadcast.status]}
                            </span>
                        </div>
                        <p className="text-sm text-base-content/70">
                            {broadcast.department?.name} · {broadcast.template?.friendlyName ?? broadcast.template?.name} ·{' '}
                            {String(broadcast.windowStartHour).padStart(2, '0')}h às{' '}
                            {String(broadcast.windowEndHour).padStart(2, '0')}h
                            {broadcast.sendOnWeekends ? ', todos os dias' : ', dias úteis'}
                        </p>
                    </div>
                </div>
                {canUpdate && (
                    <div className="flex gap-2 flex-wrap">
                        {editable && (
                            <button
                                type="button"
                                className="btn btn-ghost btn-sm gap-1"
                                onClick={() => navigate(`/messaging/whatsapp-broadcasts/edit/${broadcast.id}`)}
                            >
                                <MdEdit size={16} /> Editar
                            </button>
                        )}
                        {editable && (
                            <button
                                type="button"
                                className="btn btn-ghost btn-sm gap-1"
                                disabled={!!importing}
                                onClick={() => fileRef.current?.click()}
                            >
                                <MdUploadFile size={16} /> Importar CSV
                            </button>
                        )}
                        {editable && (
                            <button
                                type="button"
                                className="btn btn-secondary btn-sm gap-1"
                                disabled={acting}
                                onClick={() => setConfirm('start')}
                            >
                                <MdPlayArrow size={16} /> {broadcast.status === 'PAUSED' ? 'Retomar' : 'Iniciar'}
                            </button>
                        )}
                        {broadcast.status === 'RUNNING' && (
                            <button
                                type="button"
                                className="btn btn-warning btn-sm gap-1"
                                disabled={acting}
                                onClick={() => runAction(() => pauseWhatsappBroadcast(broadcast.id), 'Disparo pausado')}
                            >
                                <MdPause size={16} /> Pausar
                            </button>
                        )}
                        {!closed && (
                            <button
                                type="button"
                                className="btn btn-ghost btn-sm gap-1 text-error"
                                disabled={acting}
                                onClick={() => setConfirm('cancel')}
                            >
                                <MdCancel size={16} /> Cancelar
                            </button>
                        )}
                        {canDelete && broadcast.status !== 'RUNNING' && (
                            <button
                                type="button"
                                className="btn btn-ghost btn-sm btn-square text-error"
                                title="Excluir"
                                onClick={() => setConfirm('delete')}
                            >
                                <MdDelete size={16} />
                            </button>
                        )}
                    </div>
                )}
                <input
                    ref={fileRef}
                    type="file"
                    accept=".csv,text/csv,text/plain"
                    className="hidden"
                    onChange={e => e.target.files?.[0] && handleFile(e.target.files[0])}
                />
            </div>

            {broadcast.pausedReason && broadcast.status === 'PAUSED' && (
                <div className="alert alert-warning text-sm">{broadcast.pausedReason}</div>
            )}
            {importing && (
                <div className="alert text-sm">
                    <span className="loading loading-spinner loading-sm" />
                    Importando {importing.done.toLocaleString('pt-BR')} de {importing.total.toLocaleString('pt-BR')}...
                </div>
            )}

            <div className="card bg-base-100 shadow-sm">
                <div className="card-body p-4 space-y-4">
                    <MetaLimitCard departmentId={broadcast.departmentId} limitSharePercent={broadcast.limitSharePercent} />
                    {stats && (
                        <div className="grid grid-cols-2 sm:grid-cols-4 lg:grid-cols-8 gap-3">
                            <StatCard label="Público" value={stats.total} />
                            <StatCard label="Na fila" value={stats.pending} />
                            <StatCard label="Enviados" value={stats.sent} hint={`${stats.sentLast24h} nas últimas 24h`} />
                            <StatCard label="Entregues" value={stats.delivered} />
                            <StatCard label="Lidos" value={stats.read} />
                            <StatCard label="Responderam" value={stats.replied} />
                            <StatCard label="Pulados" value={stats.skipped} />
                            <StatCard label="Falhas" value={stats.failed} />
                        </div>
                    )}
                    {broadcast.dailyCap && (
                        <p className="text-xs text-base-content/60">
                            Teto manual: {broadcast.dailyCap.toLocaleString('pt-BR')} por 24h.
                        </p>
                    )}
                    {stats?.total === 0 && editable && (
                        <div className="rounded-lg bg-base-200 p-4 text-sm space-y-1">
                            <p className="font-medium">Importe o público em CSV.</p>
                            <p className="text-base-content/70">
                                Colunas: <span className="font-mono">telefone; nome; ultima_atividade; referencia</span>. Só o
                                telefone é obrigatório. O envio vai do contato mais recente para o mais antigo, e quem já
                                conversa pelo número do setor é pulado na hora do envio.
                            </p>
                        </div>
                    )}
                </div>
            </div>

            <div className="card bg-base-100 shadow-sm">
                <div className="card-body p-4 space-y-3">
                    <div className="flex items-end justify-between flex-wrap gap-3">
                        <h2 className="font-semibold">Contatos</h2>
                        <label className="flex flex-col gap-1">
                            <span className="text-sm font-medium text-base-content/80">Situação</span>
                            <select
                                className="select select-bordered select-sm"
                                value={statusFilter}
                                onChange={e => {
                                    setStatusFilter(e.target.value as WhatsappBroadcastRecipientStatus | '')
                                    setPage(1)
                                }}
                            >
                                <option value="">Todas</option>
                                {(Object.keys(WHATSAPP_BROADCAST_RECIPIENT_STATUS_LABELS) as WhatsappBroadcastRecipientStatus[]).map(s => (
                                    <option key={s} value={s}>
                                        {WHATSAPP_BROADCAST_RECIPIENT_STATUS_LABELS[s]}
                                    </option>
                                ))}
                            </select>
                        </label>
                    </div>
                    <div className="overflow-x-auto">
                        <table className="table table-zebra table-sm">
                            <thead>
                                <tr>
                                    <th>Telefone</th>
                                    <th>Nome</th>
                                    <th>Última atividade</th>
                                    <th>Situação</th>
                                    <th>Enviado</th>
                                    <th>Entregue / lido</th>
                                    <th>Respondeu</th>
                                    <th>Detalhe</th>
                                </tr>
                            </thead>
                            <tbody>
                                {recipients.map(r => (
                                    <tr key={r.id}>
                                        <td className="font-mono text-xs">{r.phoneNumber}</td>
                                        <td>{r.name ?? '-'}</td>
                                        <td>{r.lastActivityAt ? new Date(r.lastActivityAt).toLocaleDateString('pt-BR') : '-'}</td>
                                        <td>{WHATSAPP_BROADCAST_RECIPIENT_STATUS_LABELS[r.status]}</td>
                                        <td>{formatDateTime(r.sentAt)}</td>
                                        <td>{r.readAt ? 'Lido' : r.deliveredAt ? 'Entregue' : '-'}</td>
                                        <td>{formatDateTime(r.repliedAt)}</td>
                                        <td className="text-xs text-base-content/70 max-w-xs">
                                            {r.skipReason ?? (r.errorCode ? `(${r.errorCode}) ` : '') + (r.errorTitle ?? '')}
                                        </td>
                                    </tr>
                                ))}
                                {recipients.length === 0 && (
                                    <tr>
                                        <td colSpan={8} className="text-center text-base-content/60 py-6">
                                            Nenhum contato.
                                        </td>
                                    </tr>
                                )}
                            </tbody>
                        </table>
                    </div>
                    {pages > 1 && (
                        <div className="flex justify-end items-center gap-2 text-sm">
                            <button type="button" className="btn btn-ghost btn-xs" disabled={page <= 1} onClick={() => setPage(p => p - 1)}>
                                Anterior
                            </button>
                            <span>
                                {page} de {pages}
                            </span>
                            <button
                                type="button"
                                className="btn btn-ghost btn-xs"
                                disabled={page >= pages}
                                onClick={() => setPage(p => p + 1)}
                            >
                                Próxima
                            </button>
                        </div>
                    )}
                </div>
            </div>

            <ConfirmModal
                isOpen={confirm === 'start'}
                onClose={() => setConfirm(null)}
                loading={acting}
                title={broadcast.status === 'PAUSED' ? 'Retomar o disparo?' : 'Iniciar o disparo?'}
                message={`O envio começa no próximo ciclo dentro da janela de horário, em ritmo controlado pelo limite da Meta. ${
                    stats ? `${stats.pending.toLocaleString('pt-BR')} contatos na fila.` : ''
                }`}
                confirmButtonText={broadcast.status === 'PAUSED' ? 'Retomar' : 'Iniciar'}
                confirmButtonClass="btn-secondary"
                onConfirm={() => runAction(() => startWhatsappBroadcast(broadcast.id), 'Disparo em andamento')}
            />
            <ConfirmModal
                isOpen={confirm === 'cancel'}
                onClose={() => setConfirm(null)}
                loading={acting}
                title="Cancelar o disparo?"
                message="Os contatos que ainda estão na fila não recebem mais. Não dá para retomar um disparo cancelado."
                confirmButtonText="Cancelar disparo"
                confirmButtonClass="btn-error"
                onConfirm={() => runAction(() => cancelWhatsappBroadcast(broadcast.id), 'Disparo cancelado')}
            />
            <ConfirmModal
                isOpen={confirm === 'delete'}
                onClose={() => setConfirm(null)}
                loading={acting}
                title="Excluir o disparo?"
                message="O disparo sai da lista. O que já foi enviado não é desfeito."
                confirmButtonText="Excluir"
                confirmButtonClass="btn-error"
                onConfirm={async () => {
                    setActing(true)
                    const res = await deleteWhatsappBroadcast(broadcast.id)
                    setActing(false)
                    if (res.success) {
                        toast.success('Disparo excluído')
                        navigate('/messaging/whatsapp-broadcasts')
                    } else {
                        toast.error(res.error || 'Não foi possível excluir')
                        setConfirm(null)
                    }
                }}
            />
        </div>
    )
}

export default WhatsappBroadcastDetailPage
